"use client";

import { create } from "zustand";
import { persist } from "zustand/middleware";
import { toast } from "sonner";

// Define the cart item interface
interface CartItem {
  id: number;
  name: string;
  price: number;
  quantity: number;
  image_url: string;
  variant_id?: string;
  variant_name?: string;
}

interface Product {
  id: number;
  name: string;
  price: number;
  image_url: string;
  variant_id?: string;
  variant_name?: string;
  variant_attributes?: Array<{
    name: string;
    value: string;
  }>;
  stock?: number;
}

interface CartStore {
  items: CartItem[];
  addItem: (item: Product) => void;
  addItemWithQuantity: (item: Product, quantity: number) => void;
  removeItem: (id: number, variantId?: string) => void;
  removeAll: () => void;
  clearCart: () => void;
  totalAmount: () => number;
  totalItems: () => number;
}

export const useCart = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      
      addItem: (item: Product) => {
        const items = get().items;
        
        // Check if the item (with same variant) is already in the cart
        const existingItemIndex = items.findIndex(cartItem => 
          cartItem.id === item.id && 
          (item.variant_id ? cartItem.variant_id === item.variant_id : true)
        );
        
        if (existingItemIndex !== -1) {
          // Item exists, increase quantity
          const updatedItems = [...items];
          updatedItems[existingItemIndex].quantity += 1;
          
          set({ items: updatedItems });
          toast.success(`${item.name} quantity increased`);
        } else {
          // Add new item
          const cartItem: CartItem = {
            ...item,
            quantity: 1
          };
          
          set((state) => ({ items: [...state.items, cartItem] }));
          
          const variantLabel = item.variant_name ? ` (${item.variant_name})` : '';
          toast.success(`${item.name}${variantLabel} added to cart`);
        }
      },
      
      addItemWithQuantity: (item: Product, quantity: number) => {
        if (quantity <= 0) return;
        
        const items = get().items;
        
        // Check if the item (with same variant) is already in the cart
        const existingItemIndex = items.findIndex(cartItem => 
          cartItem.id === item.id && 
          (item.variant_id ? cartItem.variant_id === item.variant_id : true)
        );
        
        if (existingItemIndex !== -1) {
          // Item exists, increase quantity by the specified amount
          const updatedItems = [...items];
          updatedItems[existingItemIndex].quantity += quantity;
          
          set({ items: updatedItems });
          toast.success(`${quantity} ${item.name} added to cart`);
        } else {
          // Add new item with specified quantity
          const cartItem: CartItem = {
            ...item,
            quantity: quantity
          };
          
          set((state) => ({ items: [...state.items, cartItem] }));
          
          const variantLabel = item.variant_name ? ` (${item.variant_name})` : '';
          toast.success(`${quantity} ${item.name}${variantLabel} added to cart`);
        }
      },
      
      removeItem: (id: number, variantId?: string) => {
        const items = get().items;
        
        // Find the item to remove, considering variant if provided
        const itemIndex = items.findIndex(item => {
          if (variantId) {
            return item.id === id && item.variant_id === variantId;
          }
          return item.id === id;
        });
        
        if (itemIndex !== -1) {
          const item = items[itemIndex];
          
          // If quantity is more than 1, decrease quantity by 1
          if (item.quantity > 1) {
            const newItems = [...items];
            newItems[itemIndex] = {
              ...item,
              quantity: item.quantity - 1
            };
            set({ items: newItems });
            toast.success(`Quantity reduced to ${item.quantity - 1}`);
          } 
          // If quantity is 1, remove the item entirely
          else {
            const newItems = [...items];
            newItems.splice(itemIndex, 1);
            set({ items: newItems });
            toast.success("Item removed from cart");
          }
        }
      },
      
      removeAll: () => {
        const itemCount = get().items.length;
        if (itemCount > 0) {
          set({ items: [] });
          toast.success("All items removed from cart");
        }
      },
      
      clearCart: () => {
        set({ items: [] });
        toast.success("Cart cleared");
      },
      
      totalAmount: () => {
        return get().items.reduce((total, item) => {
          return total + (item.price * item.quantity);
        }, 0);
      },
      
      totalItems: () => {
        return get().items.reduce((count, item) => {
          return count + item.quantity;
        }, 0);
      }
    }),
    {
      name: "cart-storage",
    }
  )
);